import type { LocationQueryRaw } from "vue-router";

type FilterValue =
  | string
  | number
  | boolean
  | Array<string | number>
  | undefined;

type FilterDefinition = {
  default?: FilterValue;
  transform?: (value: LocationQueryRaw[string]) => FilterValue;
};

type FilterRegistry = Record<string, FilterDefinition>;

type Filters<R extends Record<string, FilterDefinition>> = {
  [key in keyof R]: R[key]["default"] extends FilterValue
    ? R[key]["default"]
    : R[key]["transform"] extends object
      ? ReturnType<R[key]["transform"]> | undefined
      : FilterValue | undefined;
};

const buildInitialFilters = <R extends FilterRegistry>(
  registry: R,
  query: LocationQueryRaw,
) => {
  return Object.entries(registry).reduce((output, [key, definition]) => {
    const transformer =
      definition.transform ?? ((value) => value as FilterValue);
    const queryValue = transformer(query[key]);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    output[key as keyof R] = queryValue ?? definition.default;

    return output;
  }, {} as Filters<R>);
};

export const useUrlFilters = <R extends FilterRegistry>(registry: R) => {
  const route = useRoute();
  const router = useRouter();

  const filters = reactive<Filters<R>>(
    buildInitialFilters(registry, route.query),
  );

  function reset() {
    Object.keys(filters).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      filters[key] = registry[key]?.default;
    });
  }

  const numberOfActiveFilters = computed(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if (Array.isArray(value)) return value.length > 0;

      return registry[key]!.default !== value;
    }).length;
  });

  const hasActiveFilters = computed(() => {
    return numberOfActiveFilters.value > 0;
  });

  watch(
    filters,
    (value) => router.replace({ query: value as LocationQueryRaw }),
    { deep: true },
  );

  return { filters, reset, hasActiveFilters, numberOfActiveFilters };
};
